// src/components/files/ShareDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  alpha,
  Autocomplete,
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import PersonIcon from '@mui/icons-material/Person';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import * as fileService from '../../services/fileService';
import { FolderShare } from '../../types/files';
import api from '../../services/api';

interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  folderId: number;
  folderName: string;
}

interface UserOption {
  id: number;
  name: string;
  email: string;
  avatar_url?: string | null;
}

type Permission = 'read' | 'write' | 'admin';

const ShareDialog: React.FC<ShareDialogProps> = ({
  open,
  onClose,
  folderId,
  folderName,
}) => {
  const { t } = useTranslation();

  const [shares, setShares] = useState<FolderShare[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // User search
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  // New share form
  const [permission, setPermission] = useState<Permission>('read');
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (open) {
      loadShares();
      setSelectedUser(null);
      setInputValue('');
      setPermission('read');
      setError(null);
      setSuccess(null);
      setUserOptions([]);
    }
  }, [open, folderId]);

  // Search users when input changes
  useEffect(() => {
    const searchUsers = async () => {
      if (inputValue.length < 2) {
        setUserOptions([]);
        return;
      }

      setSearchLoading(true);
      try {
        const response = await api.get<UserOption[]>('/users/', {
          params: { search: inputValue, limit: 10 },
        });
        setUserOptions(response.data);
      } catch (err) {
        console.error('Failed to search users:', err);
        setUserOptions([]);
      } finally {
        setSearchLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounceTimer);
  }, [inputValue]);

  const loadShares = async () => {
    setLoading(true);
    try {
      const data = await fileService.getFolderShares(folderId);
      setShares(data);
    } catch (err) {
      console.error('Failed to load shares:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddShare = async () => {
    if (!selectedUser) {
      setError(t('fileManager.share.selectUser', 'Veuillez selectionner un utilisateur'));
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await fileService.shareFolder(folderId, {
        shared_with_user_id: selectedUser.id,
        permission,
      });
      setSelectedUser(null);
      setInputValue('');
      setSuccess(t('fileManager.share.success', 'Partage ajoute avec succes'));
      await loadShares();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('fileManager.share.error', 'Erreur lors du partage'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePermission = async (shareId: number, newPermission: Permission) => {
    try {
      await fileService.updateShare(folderId, shareId, newPermission);
      await loadShares();
    } catch (err) {
      console.error('Failed to update share:', err);
    }
  };

  const handleRevokeShare = async (shareId: number) => {
    try {
      await fileService.revokeShare(folderId, shareId);
      await loadShares();
    } catch (err) {
      console.error('Failed to revoke share:', err);
    }
  };

  const handleCopyLink = () => {
    const link = `${window.location.origin}/files/shared/${folderId}`;
    navigator.clipboard.writeText(link);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ShareIcon color="primary" />
        {t('fileManager.share.title', 'Partager')} "{folderName}"
      </DialogTitle>

      <DialogContent>
        {/* Add new share */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            {t('fileManager.share.addPeople', 'Ajouter des personnes')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Autocomplete
              size="small"
              fullWidth
              options={userOptions}
              loading={searchLoading}
              value={selectedUser}
              onChange={(_, newValue) => setSelectedUser(newValue)}
              inputValue={inputValue}
              onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
              getOptionLabel={(option) => option.name || option.email}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(x) => x} // Disable built-in filtering, use server results
              noOptionsText={
                inputValue.length < 2
                  ? t('fileManager.share.typeToSearch', 'Tapez au moins 2 caracteres')
                  : t('fileManager.share.noUsersFound', 'Aucun utilisateur trouve')
              }
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ display: 'flex', gap: 1.5, py: 1 }}>
                  <Avatar
                    src={option.avatar_url || undefined}
                    sx={{ width: 32, height: 32, bgcolor: alpha('#2563eb', 0.1) }}
                  >
                    <PersonIcon sx={{ fontSize: 18, color: '#2563eb' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {option.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.email}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t('fileManager.share.searchPlaceholder', 'Rechercher par nom ou email')}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {searchLoading ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              disabled={saving}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                value={permission}
                onChange={(e) => setPermission(e.target.value as Permission)}
                disabled={saving}
              >
                <MenuItem value="read">{t('fileManager.share.canView', 'Peut voir')}</MenuItem>
                <MenuItem value="write">{t('fileManager.share.canEdit', 'Peut modifier')}</MenuItem>
                <MenuItem value="admin">{t('fileManager.share.admin', 'Admin')}</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={handleAddShare}
              disabled={saving || !selectedUser}
              sx={{ minWidth: 100 }}
            >
              {saving ? <CircularProgress size={20} /> : t('fileManager.share.add', 'Ajouter')}
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mt: 1 }}>
              {success}
            </Alert>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Current shares */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            {t('fileManager.share.peopleWithAccess', 'Personnes avec acces')} ({shares.length})
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : shares.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              {t('fileManager.share.noShares', 'Aucun partage pour le moment')}
            </Typography>
          ) : (
            <List dense disablePadding>
              {shares.map((share) => (
                <ListItem
                  key={share.id}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    '&:hover': { bgcolor: 'grey.50' },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: alpha('#2563eb', 0.1), color: '#2563eb' }}>
                      <PersonIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={share.shared_with?.name || share.shared_with?.email}
                    secondary={share.shared_with?.email}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <Select
                        value={share.permission}
                        onChange={(e) => handleUpdatePermission(share.id, e.target.value as Permission)}
                        size="small"
                      >
                        <MenuItem value="read">{t('fileManager.share.canView', 'Peut voir')}</MenuItem>
                        <MenuItem value="write">{t('fileManager.share.canEdit', 'Peut modifier')}</MenuItem>
                        <MenuItem value="admin">{t('fileManager.share.admin', 'Admin')}</MenuItem>
                      </Select>
                    </FormControl>
                    <IconButton
                      size="small"
                      onClick={() => handleRevokeShare(share.id)}
                      sx={{ color: 'error.main' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Copy link */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            {t('fileManager.share.getLink', 'Obtenir le lien')}
          </Typography>
          <Button
            variant="outlined"
            startIcon={linkCopied ? <CheckIcon /> : <ContentCopyIcon />}
            onClick={handleCopyLink}
            sx={{ color: linkCopied ? 'success.main' : 'inherit' }}
          >
            {linkCopied
              ? t('fileManager.share.linkCopied', 'Lien copie!')
              : t('fileManager.share.copyLink', 'Copier le lien')}
          </Button>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>
          {t('common.close', 'Fermer')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareDialog;
